<template>
  <!--
    Single iframe that hosts the React chatbot.
    It is always mounted and controlled only via postMessage.
  -->
  <iframe
    ref="iframeRef"
    :src="CHATBOT_ORIGIN"
    title="Chatbot"
    class="chatbot-iframe"
    :class="{
      'rounded-full': size.width === '56px',
      'rounded-2xl': size.width !== '56px'
    }"
    :style="computedStyle"
    allow="clipboard-write; microphone"
  />
</template>

<script>
import { getOrCreateGuestUserId } from "src/utils/guestUser";
import { CHATBOT_ORIGIN } from "src/config/chatbotConfig";

export default {
  name: "ChatbotIframe",

  /**
   * User comes from App.vue (which reads Vuex state)
   * null  -> visitor
   * object -> logged-in user
   */
  props: {
    user: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      CHATBOT_ORIGIN,
      iframeRef: null,

      // Default collapsed size (floating button)
      size: {
        width: "56px",
        height: "56px"
      },

      // Handshake flag
      isChatbotReady: false,

      // Mobile detection
      isMobile: window.innerWidth <= 550
    };
  },

  computed: {
    /**
     * On mobile, expand chatbot to near-fullscreen
     */
    computedStyle() {
      if (this.size.width !== "56px" && this.isMobile) {
        return {
          width: "85%",
          height: "-webkit-fill-available"
        };
      }
      return this.size;
    }
  },

  mounted() {
    console.log("[ChatbotIframe] mounted");

    this.iframeRef = this.$refs.iframeRef;

    window.addEventListener("resize", this.handleResize);
    window.addEventListener("message", this.onMessage);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("message", this.onMessage);
  },

  watch: {
    /**
     * 🔥 CRITICAL WATCHER
     * This fires whenever Vuex link data changes
     * (login, logout, refresh, retry, etc.)
     */
    user(newUser) {
      if (!this.isChatbotReady) return;

      console.log("[ChatbotIframe] user changed → re-init chat", newUser);

      this.sendInitChat(newUser);
    }
  },

  methods: {
    /* -------------------------
       Window resize handler
    ------------------------- */
    handleResize() {
      this.isMobile = window.innerWidth <= 550;
    },

    /* -------------------------
       postMessage listener
    ------------------------- */
    onMessage(event) {
      // Security: only accept messages from chatbot origin
      if (event.origin !== CHATBOT_ORIGIN) return;
      if (!event.data || !event.data.type) return;

      const { type, reSize } = event.data;

      // 1️⃣ Chatbot ready handshake
      if (type === "CHATBOT_READY") {
        // Prevent double initialization
        if (this.isChatbotReady) return;

        console.log("[ChatbotIframe] CHATBOT_READY");

        this.isChatbotReady = true;

        // Initial init using current user state
        this.sendInitChat(this.user);
      }

      // 2️⃣ Chatbot opened (expand iframe)
      if (type === "CHATBOT_OPEN") {
        console.log("[ChatbotIframe] CHATBOT_OPEN", reSize);
        this.size = reSize;
      }

      // 3️⃣ Chatbot closed (collapse iframe)
      if (type === "CHATBOT_CLOSE") {
        console.log("[ChatbotIframe] CHATBOT_CLOSE");
        this.size = { width: "56px", height: "56px" };
      }
    },

    /* -------------------------
       INIT_CHAT sender (CORE)
       - mode ALWAYS onboarding
       - user fields only if logged in
    ------------------------- */
    sendInitChat(user) {
      const isLoggedIn = !!(user && user._id);

      const payload = {
        type: "INIT_CHAT",
        userId: isLoggedIn ? user._id : getOrCreateGuestUserId(),
        mode: "onboarding" // ✅ ALWAYS
      };

      // Attach user info ONLY if logged in
      if (isLoggedIn) {
        payload.email = user.email;
        payload.fname = user.fname;
        payload.lname = user.lname;
      }
      console.log("user", user);
      console.log("[ChatbotIframe] INIT_CHAT payload", payload);

      this.iframeRef.contentWindow.postMessage(payload, CHATBOT_ORIGIN);
    }
  }
};
</script>

<style scoped>
/* Floating iframe positioning */
.chatbot-iframe {
  position: fixed;
  bottom: 20px;
  right: 20px;
  border: none;
  z-index: 999999;
}

/* Collapsed (button) state */
.rounded-full {
  border-radius: 50%;
}

/* Expanded chat window */
.rounded-2xl {
  border-radius: 16px;
}
</style>
